/* eslint-disable prefer-arrow-callback */
import { type Dispatch, type MutableRefObject, type SetStateAction, useCallback, useEffect, useRef } from "react";

import type { DroppedMainCrop } from "models";

import type { useFieldImportLandingState } from "pages/app/scenario-analysis/hooks/useFieldImportLandingState";
import type { ScenarioAnalysisModal } from "pages/app/scenario-analysis/hooks/useModalState";
import { useSoilSightHydration } from "pages/app/scenario-analysis/hooks/useSoilSightHydration";
import { useSoilSightProjectSession } from "pages/app/scenario-analysis/hooks/useSoilSightProjectSession";
import { type ProjectRefinementsState, type ScenarioData, type ScenarioSettings } from "pages/app/scenario-analysis/types/scenario";
import { type AutosaveRefs, isFieldReselectNeeded } from "pages/app/scenario-analysis/utils/scenarioAnalysisHelpers";

type ActiveProjectLifecycleArgs = {
  klimFarmId: string;
  landing: ReturnType<typeof useFieldImportLandingState>;
  autosaveRefs: AutosaveRefs;
  hasHydratedRef: MutableRefObject<boolean>;
  hydrationApplied: boolean;
  setHydrationApplied: Dispatch<SetStateAction<boolean>>;
  /** Resets the page state that belongs to the project being left. */
  onProjectReset: () => void;
  showTransitionModal: boolean;
  setShowTransitionModal: (show: boolean) => void;
  setAwaitingInitialCalc: Dispatch<SetStateAction<boolean>>;
  setDroppedMainCrops: (crops: DroppedMainCrop[]) => void;
  openModal: (modal: ScenarioAnalysisModal) => void;
  closeModal: (modal: ScenarioAnalysisModal) => void;
  setSettings: Dispatch<SetStateAction<ScenarioSettings>>;
  setProjectRefinements: Dispatch<SetStateAction<ProjectRefinementsState>>;
  setScenarios: Dispatch<SetStateAction<ScenarioData[]>>;
  setHasCalculated: Dispatch<SetStateAction<boolean>>;
  setIsDirty: Dispatch<SetStateAction<boolean>>;
};

/** The active project's lifecycle on the page: its session, hydration, the field-reselect gate and the import landing bookkeeping. */
export const useActiveProjectLifecycle = ({
  klimFarmId,
  landing,
  autosaveRefs,
  hasHydratedRef,
  hydrationApplied,
  setHydrationApplied,
  onProjectReset,
  showTransitionModal,
  setShowTransitionModal,
  setAwaitingInitialCalc,
  setDroppedMainCrops,
  openModal,
  closeModal,
  setSettings,
  setProjectRefinements,
  setScenarios,
  setHasCalculated,
  setIsDirty,
}: ActiveProjectLifecycleArgs) => {
  const { isAwaitingProjectChoice, importedFieldCropYearsRef, importedSelectedFieldIdRef, fieldImportAlreadyPersistedRef } = landing;

  const session = useSoilSightProjectSession({
    klimFarmId,
    autosaveRefs,
    hasHydratedRef,
    setHydrationApplied,
    onProjectReset,
    landing,
    setShowTransitionModal,
    setAwaitingInitialCalc,
    setDroppedMainCrops,
  });
  const { soilSightProjectId, pageData } = session;
  const { projectQuery, scenarioListQuery, farmFields, refetchFarmFields, scenarioDetails, allDetailsResolved, projectFieldId, fieldsForProjectFarmLoaded } =
    pageData;

  const fieldReselectNeeded = isFieldReselectNeeded({
    isProjectLoaded: projectQuery.isSuccess,
    soilSightProjectId,
    hydrationApplied,
    isTransitionOpen: showTransitionModal,
    fieldsForProjectFarmLoaded,
    projectFieldId,
    farmFields,
    projectHasScenarios: pageData.projectHasScenarios,
  });
  const openFieldReselect = useCallback(
    function openFieldReselect() {
      openModal("fieldReselect");
    },
    [openModal]
  );
  const closeFieldReselect = useCallback(
    function closeFieldReselect() {
      closeModal("fieldReselect");
    },
    [closeModal]
  );

  // Not every delete path invalidates the 24h-cached field list, so refresh it once per project load.
  const fieldsRefreshedForProjectRef = useRef<string | null>(null);
  useEffect(
    function refreshFieldsForActiveProject() {
      if (!klimFarmId || !soilSightProjectId) return;
      if (fieldsRefreshedForProjectRef.current === soilSightProjectId) return;
      fieldsRefreshedForProjectRef.current = soilSightProjectId;
      refetchFarmFields();
    },
    [klimFarmId, soilSightProjectId, refetchFarmFields]
  );

  // An import belongs to the project it landed on; once the user moves on, later projects must not be treated as its landing.
  const importLandingProjectIdRef = useRef<string | null>(null);
  useEffect(
    function forgetImportAfterLeavingItsProject() {
      if (!soilSightProjectId) return;
      // Behind the entry dialog the shown project isn't the landing yet; the import lands once the user's choice has placed its years.
      if (isAwaitingProjectChoice && importedFieldCropYearsRef.current !== null) return;
      if (hydrationApplied && !importLandingProjectIdRef.current) importLandingProjectIdRef.current = soilSightProjectId;
      if (!importLandingProjectIdRef.current || importLandingProjectIdRef.current === soilSightProjectId) return;
      importedSelectedFieldIdRef.current = null;
      fieldImportAlreadyPersistedRef.current = false;
    },
    [fieldImportAlreadyPersistedRef, hydrationApplied, importedFieldCropYearsRef, importedSelectedFieldIdRef, isAwaitingProjectChoice, soilSightProjectId]
  );

  useSoilSightHydration({
    soilSightProjectId,
    allDetailsResolved,
    projectQuery,
    scenarioListQuery,
    scenarioDetails,
    autosaveRefs,
    setSettings,
    setProjectRefinements,
    setScenarios,
    setHasCalculated,
    setHydrationApplied,
    importAlreadyPersisted: fieldImportAlreadyPersistedRef.current,
    // While the entry dialog is open the shown project is not the import's target, so it must not take the imported field.
    importedSelectedFieldId: isAwaitingProjectChoice ? null : importedSelectedFieldIdRef.current,
  });

  // A direct import (no entry dialog) applies its field on hydration; mark dirty so autosave persists it.
  useEffect(
    function markDirtyForImportedFieldSelection() {
      if (isAwaitingProjectChoice) return;
      if (importedSelectedFieldIdRef.current) setIsDirty(true);
    },
    [importedSelectedFieldIdRef, isAwaitingProjectChoice, setIsDirty]
  );

  return { ...session, fieldReselectNeeded, openFieldReselect, closeFieldReselect };
};
